/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import request from './index';
import { BACKEND_ERROR_MESSAGE } from '../utils/constants/content-production';
import IMS from './ims';
import AppID from '../classes/AppID';
import { getActionMapping, type ActionMapping } from './permissions/actionMapper';

/**
 * TBW Authorization Service
 * -------------------------
 * Owns all permission checks against the InApp Authoring Service (IAS) `tbw` domain.
 * TBW resource shapes differ from CP — tutorial requests use camelCase `aemId`, plus
 * `stage`/`authorId`/`creatorId`, with metadata nested under `content.metadata`.
 *
 * TBW supports exactly 4 products: Express, Photoshop (Desktop), Photoshop Web, Illustrator.
 * Capability placeholders are scoped to this set.
 *
 * For CP authorization see `AuthorizationService.ts`.
 */

const BASE_INAPP_API_URL = process.env.INAPP_API_ENDPOINT
    ? process.env.INAPP_API_ENDPOINT
    : 'http://localhost:8080/api/';
const API_KEY = 'LearnContentPortal1';

interface AuthorizationRequest {
    action: string;
    resource: Record<string, unknown>;
}

interface AuthorizationResult {
    decision: 'ALLOW' | 'DENY';
    action: string;
    resource: Record<string, unknown>;
    reasons?: string[];
    determiningPolicies?: Record<string, unknown>[];
    errors?: Record<string, unknown>[];
}

interface AuthorizationResponse {
    tutorialResults?: AuthorizationResult[];
    queryResults?: AuthorizationResult[];
    tagResults?: AuthorizationResult[];
    userRecordResults?: AuthorizationResult[];
    userId?: string;
}

export interface CheckTbwCapabilitiesResult {
    permissions: Record<string, boolean>;
}

// ---------------------------------------------------------------------------
// TBW supported products + canonical resource shapes
// ---------------------------------------------------------------------------

/** The 4 products supported in Tutorial Builder Web. */
const TBW_PRODUCTS: Array<{ sap_code: string; fi_code: string }> = [
    { sap_code: AppID.Express.sapCode, fi_code: AppID.Express.id },
    { sap_code: AppID.PSDesktop.sapCode, fi_code: AppID.PSDesktop.id },
    { sap_code: AppID.PSWeb.sapCode, fi_code: AppID.PSWeb.id },
    { sap_code: AppID.Illustrator.sapCode, fi_code: AppID.Illustrator.id },
];

/** Tutorial resource shape accepted by hooks (matches CP-style for backward compat). */
export interface TbwTutorialInput {
    aem_id: string;
    status: string; // "dev" | "live"
    locale: string;
    metadata?: {
        application?: { sap_code: string; fi_code: string };
    };
    platforms?: string[];
    /**
     * Cedar workflow stage (e.g. "in-development", "ready-for-review", "ready-for-qe").
     * Always sourced from the dashboard listing's `state` field. For capability/placeholder
     * checks (no real tutorial), the service hardcodes "in-development".
     */
    stage?: string;
    /** Tutorial author GUID. Defaults to the current user. */
    authorId?: string;
    /** Tutorial creator GUID. Defaults to the current user. */
    creatorId?: string;
}

/** Resource type for the request-body grouping (mirrors the actionMapper enum). */
type ResourceType = 'tutorial' | 'query' | 'tag' | 'userRecord';

const RESOURCE_TYPE_TO_REQUEST_KEY: Record<ResourceType, keyof Pick<
    Record<string, AuthorizationRequest[]>,
    string
>> = {
    tutorial: 'tutorialRequests',
    query: 'queryRequests',
    tag: 'tagRequests',
    userRecord: 'userRecordRequests',
};

const RESOURCE_TYPE_TO_RESPONSE_KEY: Record<ResourceType, keyof AuthorizationResponse> = {
    tutorial: 'tutorialResults',
    query: 'queryResults',
    tag: 'tagResults',
    userRecord: 'userRecordResults',
};

class TbwAuthorizationService {
    /** Cached current-user GUID. TBW Cedar policies require authorId/creatorId on tutorial resources. */
    private _currentUserIdPromise: Promise<string> | null = null;

    private _getRequestHeaders = () => ({
        'x-api-key': API_KEY,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        authorization: '',
    });

    private _getBaseHeaders = () => {
        const token = IMS.getInstance().getAccessToken();
        if (!token) {
            throw new Error('Unable to get access token from IMS. User may not be authenticated.');
        }
        return {
            ...this._getRequestHeaders(),
            authorization: `Bearer ${token}`,
        };
    };

    /** Lazy-load the current user GUID (e.g. "ABC...@AdobeID") and cache it. */
    private _getCurrentUserId = (): Promise<string> => {
        if (!this._currentUserIdPromise) {
            this._currentUserIdPromise = IMS.getInstance()
                .getProfile()
                .then((result: unknown) => {
                    const r = result as { failed?: boolean; data?: { userId?: string } } | undefined;
                    if (r?.failed || !r?.data?.userId) return '';
                    return r.data.userId;
                })
                .catch(() => '');
        }
        return this._currentUserIdPromise;
    };

    // ---------------------------------------------------------------------------
    // Resource builders (TBW-specific shapes)
    // ---------------------------------------------------------------------------

    /**
     * Build a TBW tutorial Cedar resource from caller input.
     * - Renames `aem_id` → `aemId`.
     * - Defaults `authorId` / `creatorId` to the current user when not provided.
     * - Uses caller-supplied `stage` directly (no derivation). Real tutorial calls must
     *   forward the `state` field from the dashboard listing; capability/placeholder calls
     *   hardcode `stage` in `_buildTutorialPlaceholders`.
     * - Wraps `metadata` (with optional `platforms`) under `content`.
     */
    private _buildTutorialResource = (t: TbwTutorialInput, currentUserId: string): Record<string, unknown> => {
        // CP placeholder builder (used by callers in the past) emitted "draft"; TBW expects "dev".
        const status = t.status === 'draft' ? 'dev' : t.status;
        const application = t.metadata?.application;

        const contentMetadata: Record<string, unknown> = {};
        if (application !== undefined) contentMetadata['application'] = application;
        if (t.platforms != null) contentMetadata['platforms'] = t.platforms;

        return {
            aemId: t.aem_id,
            stage: t.stage,
            status,
            locale: t.locale,
            authorId: t.authorId ?? currentUserId,
            creatorId: t.creatorId ?? currentUserId,
            content: { metadata: contentMetadata },
        };
    };

    /**
     * Build placeholder tutorial resources for the given apps. When `apps` is omitted, falls back
     * to all 4 TBW products (the action allows if ANY product allows). When `apps` is provided
     * (e.g. the currently selected app on the dashboard), the check is scoped to those apps only.
     *
     * Stage is HARDCODED to "in-development" for placeholders — they represent a hypothetical
     * brand-new tutorial the user might create, which by definition starts in development.
     * Real tutorial calls must pass the actual stage via `TbwTutorialInput.stage`.
     */
    private _buildTutorialPlaceholders = (
        currentUserId: string,
        locale: string,
        apps?: Array<{ sap_code: string; fi_code: string }>,
    ): Record<string, unknown>[] => {
        const targetApps = apps?.length ? apps : TBW_PRODUCTS;
        return targetApps.map((app) =>
            this._buildTutorialResource(
                {
                    aem_id: `tutorial/placeholder-id-${app.fi_code}`,
                    status: 'dev',
                    locale,
                    metadata: { application: app },
                    stage: 'in-development',
                },
                currentUserId,
            ),
        );
    };

    // ---------------------------------------------------------------------------
    // HTTP plumbing
    // ---------------------------------------------------------------------------

    private _getAuthorizationUrl = (): string => `${BASE_INAPP_API_URL}/v1/domains/tbw/authorization`;

    private async _makeBatchRequest(
        requestBody: Record<string, AuthorizationRequest[]>,
    ): Promise<AuthorizationResponse | null> {
        try {
            const headers = this._getBaseHeaders();
            const url = this._getAuthorizationUrl();
            const responseData = await request(url, headers, BACKEND_ERROR_MESSAGE, {}, 'POST', requestBody);
            if (responseData?.failed) {
                console.error('tbw batch authorization failed:', responseData);
                return null;
            }
            return responseData?.data as AuthorizationResponse;
        } catch (error) {
            console.error(`${BACKEND_ERROR_MESSAGE} tbw > batch authorization > ${error}`);
            return null;
        }
    }

    // ---------------------------------------------------------------------------
    // Public API
    // ---------------------------------------------------------------------------

    /**
     * Check tutorial-resource permissions for a batch of (action × tutorial) pairs.
     * Result layout: results[i * tutorials.length + j] = actions[i] applied to tutorials[j].
     */
    async checkTutorialPermissions(actions: string[], tutorials: TbwTutorialInput[]): Promise<boolean[]> {
        const totalPairs = actions.length * tutorials.length;
        if (actions.length === 0 || tutorials.length === 0) {
            return Array(totalPairs).fill(false);
        }

        // Filter to tutorials that have application metadata (Cedar policy needs it).
        const validIndices = tutorials.map((t, i) => (t.metadata?.application ? i : -1)).filter((i) => i >= 0);
        if (validIndices.length === 0) {
            return Array(totalPairs).fill(false);
        }

        const userId = await this._getCurrentUserId();
        const validResources = validIndices.map((i) => this._buildTutorialResource(tutorials[i], userId));

        // Validate all action mappings up-front.
        const validatedActions = actions.map((action) => {
            const mapping = getActionMapping(action, 'tbw');
            if (!mapping || mapping.domain !== 'tbw' || mapping.resourceType !== 'tutorial') {
                throw new Error(
                    `[TbwAuthorizationService] Action "${action}" is not a TBW tutorial action`,
                );
            }
            return mapping;
        });

        // Cartesian product: action i × tutorial j → request body item at (i * V + k) where k is the
        // valid-tutorial index. Backend returns results in the same order.
        const tutorialRequests: AuthorizationRequest[] = [];
        validatedActions.forEach((mapping) => {
            validResources.forEach((resource) => {
                tutorialRequests.push({ action: mapping.backendAction, resource });
            });
        });

        const response = await this._makeBatchRequest({ tutorialRequests });
        const apiResults = response?.tutorialResults;

        if (!apiResults) {
            return Array(totalPairs).fill(false);
        }

        // Map results back to the full (action × tutorial) grid; invalid tutorials get false.
        const fullResults: boolean[] = [];
        const V = validIndices.length;
        for (let i = 0; i < totalPairs; i++) {
            const actionIdx = Math.floor(i / tutorials.length);
            const tutorialIdx = i % tutorials.length;
            const k = validIndices.indexOf(tutorialIdx);
            if (k < 0) {
                fullResults.push(false);
            } else {
                fullResults.push(apiResults[actionIdx * V + k]?.decision === 'ALLOW');
            }
        }
        return fullResults;
    }

    /**
     * Check capability/placeholder permissions (no specific tutorial). Supports query-type
     * actions that need `{ locale, applications }` resources (e.g. list-tutorials-as-*) and
     * tutorial-create capability checks via placeholder resources for the 4 TBW products.
     */
    async checkCapabilities(
        actions: string[],
        options?: { locale?: string; applications?: Array<{ sap_code: string; fi_code: string }> },
    ): Promise<CheckTbwCapabilitiesResult> {
        if (actions.length === 0) return { permissions: {} };

        const mappings: Array<{ action: string; mapping: ActionMapping }> = actions
            .map((action) => {
                const mapping = getActionMapping(action, 'tbw');
                if (!mapping || mapping.domain !== 'tbw') return null;
                return { action, mapping };
            })
            .filter((x): x is { action: string; mapping: ActionMapping } => x !== null);

        if (mappings.length === 0) {
            return { permissions: this._defaultPermissions(actions) };
        }

        // Fetch user GUID once (only needed if we generate tutorial placeholders).
        const userIdPromise = mappings.some((m) => m.mapping.resourceType === 'tutorial')
            ? this._getCurrentUserId()
            : Promise.resolve('');

        const currentUserId = await userIdPromise;
        // For tutorial placeholders, prefer the caller-supplied apps so the capability is scoped
        // to the current selection (a user may be Author on App A but only QE on App B). Fallback
        // to all 4 TBW products when apps aren't supplied.
        const tutorialPlaceholders = this._buildTutorialPlaceholders(
            currentUserId,
            options?.locale ?? 'en_US',
            options?.applications,
        );
        const queryResource = this._buildQueryResource(options);

        // Group request items by resource type. Track which (action, mapping) each emitted item
        // corresponds to so we can extract results later.
        const requestBody: Record<string, AuthorizationRequest[]> = {};
        const emittedPerType: Record<ResourceType, Array<{ action: string; mapping: ActionMapping }>> = {
            tutorial: [],
            query: [],
            tag: [],
            userRecord: [],
        };

        mappings.forEach(({ action, mapping }) => {
            const rt = mapping.resourceType as ResourceType;
            const requestKey = RESOURCE_TYPE_TO_REQUEST_KEY[rt];
            if (!requestKey) return;
            if (!requestBody[requestKey]) requestBody[requestKey] = [];

            // Resource selection per type:
            // - tutorial: emit one item per TBW product placeholder (caller is allowed if ANY product allows)
            // - query: emit one item with `{ locale, applications }` (or `{}` if no options provided)
            // - tag/userRecord: emit one item with empty `{}` placeholder
            if (rt === 'tutorial' && mapping.requiresResource) {
                tutorialPlaceholders.forEach((res) => {
                    requestBody[requestKey].push({ action: mapping.backendAction, resource: res });
                    emittedPerType.tutorial.push({ action, mapping });
                });
            } else if (rt === 'query') {
                requestBody[requestKey].push({ action: mapping.backendAction, resource: queryResource });
                emittedPerType.query.push({ action, mapping });
            } else {
                requestBody[requestKey].push({ action: mapping.backendAction, resource: {} });
                emittedPerType[rt].push({ action, mapping });
            }
        });

        const response = await this._makeBatchRequest(requestBody);
        const permissions = this._defaultPermissions(actions);

        if (!response) return { permissions };

        // For each resource type, walk the emitted items and the API results in lock-step. A
        // single action may have emitted multiple items (tutorial placeholders); the action is
        // ALLOW if ANY of its emitted items allowed.
        (Object.keys(emittedPerType) as ResourceType[]).forEach((rt) => {
            const items = emittedPerType[rt];
            if (items.length === 0) return;
            const apiResults = response[RESOURCE_TYPE_TO_RESPONSE_KEY[rt]] as AuthorizationResult[] | undefined;
            if (!apiResults) return;

            items.forEach((item, idx) => {
                if (apiResults[idx]?.decision === 'ALLOW') {
                    permissions[item.action] = true;
                }
            });
        });

        return { permissions };
    }

    /**
     * Per-app ALLOW/DENY for a single tutorial action (unlike `checkCapabilities` which
     * OR-aggregates). Returns a map keyed by `AppID.appName`.
     */
    async checkTutorialActionPerApp(
        action: string,
        locale: string,
        apps: Array<{ sap_code: string; fi_code: string }>,
    ): Promise<Record<string, boolean>> {
        if (apps.length === 0) return {};

        const mapping = getActionMapping(action, 'tbw');
        if (!mapping || mapping.domain !== 'tbw' || mapping.resourceType !== 'tutorial') {
            throw new Error(`[TbwAuthorizationService] Action "${action}" is not a TBW tutorial action`);
        }

        const userId = await this._getCurrentUserId();
        const placeholders = this._buildTutorialPlaceholders(userId, locale, apps);

        const tutorialRequests: AuthorizationRequest[] = placeholders.map((resource) => ({
            action: mapping.backendAction,
            resource,
        }));

        const response = await this._makeBatchRequest({ tutorialRequests });
        const apiResults = response?.tutorialResults;

        const result: Record<string, boolean> = {};
        apps.forEach((app, i) => {
            const appId = AppID.find(app.fi_code);
            const appName = appId?.appName ?? app.sap_code;
            result[appName] = apiResults?.[i]?.decision === 'ALLOW';
        });

        return result;
    }

    /** Resource shape for query-type actions (LIST_TUTORIALS_AS_*). */
    private _buildQueryResource = (options?: {
        locale?: string;
        applications?: Array<{ sap_code: string; fi_code: string }>;
    }): Record<string, unknown> => {
        if (!options?.locale && !options?.applications?.length) return {};
        return {
            locale: options.locale ?? 'en_US',
            applications: options.applications ?? [],
        };
    };

    /** All-false permissions map for a list of actions. */
    private _defaultPermissions = (actions: string[]): Record<string, boolean> => {
        const map: Record<string, boolean> = {};
        actions.forEach((a) => {
            map[a] = false;
        });
        return map;
    };

    /**
     * "Can the user view Tutorial Builder?" — true when GET_TAGS is allowed (any TBW role).
     */
    async canViewTutorialBuilder(): Promise<boolean> {
        const result = await this.checkCapabilities(['view-tutorial-builder-web']);
        return result.permissions['view-tutorial-builder-web'] === true;
    }

    /**
     * TBW arm of the cross-domain User Management visibility check. Caller (appSlice) OR's this
     * with the LCP arm in `AuthorizationService.canViewUserManagementInLCP`.
     */
    async canViewUserManagementInTBW(): Promise<boolean> {
        const requestBody: Record<string, AuthorizationRequest[]> = {
            userRecordRequests: [{ action: 'LIST_USER_RECORDS', resource: { entityType: 'user' } }],
        };
        const response = await this._makeBatchRequest(requestBody);
        return response?.userRecordResults?.[0]?.decision === 'ALLOW';
    }
}

const tbwAuthorizationService = new TbwAuthorizationService();
export default tbwAuthorizationService;
